<template>
  <div class="surplus-manager-dashboard">
    <!-- Header -->
    <dashboard-context-header title="GESTION SURPLUS" @context-change="onContextChange" />

    <!-- Feature désactivée -->
    <v-alert v-if="!featureSurplus" type="info" class="ma-4">
      Le module gestion des surplus n'est pas activé pour cette cantine.
    </v-alert>

    <!-- Cartes principales -->
    <div v-if="featureSurplus" class="cards-grid">
      <!-- Card 1: Nouvelles offres -->
      <div class="card card-featured" @click="showAllOffers = true">
        <div class="card-header">
          <h3 class="card-title">🔔 Nouvelles offres</h3>
          <span class="badge badge-orange">{{ surplusOffers.length }}</span>
        </div>
        <p class="card-description">Surplus disponibles à collecter aujourd'hui</p>
        <button class="card-link">Voir les offres →</button>
      </div>

      <!-- Card 2: Mes réservations -->
      <div class="card" @click="showReservations = true">
        <div class="card-header">
          <h3 class="card-title">📋 Mes réservations</h3>
          <span class="badge badge-blue">{{ myReservations.length }} en cours</span>
        </div>
        <p class="card-description">Collectes prévues et historique</p>
        <button class="card-link">Consulter →</button>
      </div>

      <!-- Card 3: Statistiques -->
      <div class="card" @click="showStats = true">
        <div class="card-header">
          <h3 class="card-title">📊 Mes statistiques</h3>
          <span class="badge badge-green">Ce mois</span>
        </div>
        <p class="card-description">Impact et denrées valorisées</p>
        <button class="card-link">Voir →</button>
      </div>

      <!-- Card 4: Historique -->
      <div class="card" @click="showHistory = true">
        <div class="card-header">
          <h3 class="card-title">📅 Historique</h3>
        </div>
        <p class="card-description">Toutes mes collectes passées</p>
        <button class="card-link">Consulter →</button>
      </div>
    </div>

    <!-- Modal: Toutes les offres -->
    <div v-if="showAllOffers" class="modal-overlay" @click="showAllOffers = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showAllOffers = false">✕</button>
        <h2 class="modal-title">🔔 Nouvelles offres ({{ surplusOffers.length }})</h2>
        <p class="modal-subtitle">Surplus disponibles aujourd'hui</p>

        <div v-if="offersLoading" style="text-align:center;color:#6b7280;padding:1rem">Chargement…</div>
        <div v-else class="offers-list">
          <div v-for="offer in surplusOffers" :key="offer.id" class="offer-card" @click="viewOfferDetails(offer)">
            <div class="offer-header">
              <h4 class="offer-school">🏫 {{ offer.canteenName || offer.canteen_name || "—" }}</h4>
            </div>
            <div class="offer-details">
              <div class="offer-quantity">
                📦 {{ offer.items && offer.items.quantity_kg ? offer.items.quantity_kg + " kg" : "—" }}
              </div>
              <div class="offer-items">{{ offer.items && offer.items.dish_name ? offer.items.dish_name : "—" }}</div>
            </div>
            <div class="offer-footer">
              <span class="offer-time">📅 {{ offer.mealDate || offer.meal_date }}</span>
              <button @click.stop="reserveOffer(offer)" :disabled="reservingId === offer.id" class="btn-reserve">
                {{ reservingId === offer.id ? "…" : "✅ Réserver" }}
              </button>
              <button
                @click.stop="cancelOffer(offer)"
                :disabled="cancellingOfferId === offer.id"
                class="btn-cancel-offer"
              >
                {{ cancellingOfferId === offer.id ? "…" : "✕ Annuler l'offre" }}
              </button>
            </div>
          </div>
          <p v-if="surplusOffers.length === 0" style="text-align:center;color:#6b7280">Aucune offre disponible.</p>
        </div>
      </div>
    </div>

    <!-- Modal: Détail d'une offre -->
    <div v-if="selectedOffer" class="modal-overlay" @click="selectedOffer = null">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="selectedOffer = null">✕</button>
        <h2 class="modal-title">{{ selectedOffer.canteenName || selectedOffer.canteen_name }}</h2>

        <div class="detail-section">
          <h3 class="section-title">Détails du surplus</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Quantité</span>
              <span class="info-value">
                {{
                  selectedOffer.items && selectedOffer.items.quantity_kg ? selectedOffer.items.quantity_kg + " kg" : "—"
                }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Denrée</span>
              <span class="info-value">
                {{ selectedOffer.items && selectedOffer.items.dish_name ? selectedOffer.items.dish_name : "—" }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Date</span>
              <span class="info-value">{{ selectedOffer.mealDate || selectedOffer.meal_date }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">État</span>
              <span class="info-value">
                {{ selectedOffer.items && selectedOffer.items.state ? selectedOffer.items.state : "—" }}
              </span>
            </div>
          </div>
        </div>

        <button
          @click="reserveOffer(selectedOffer)"
          :disabled="reservingId === selectedOffer.id"
          class="btn btn-primary"
        >
          {{ reservingId === selectedOffer.id ? "Réservation en cours…" : "✅ Réserver cette offre" }}
        </button>
        <button
          @click="cancelOffer(selectedOffer)"
          :disabled="cancellingOfferId === selectedOffer.id"
          class="btn btn-danger"
        >
          {{ cancellingOfferId === selectedOffer.id ? "Annulation…" : "✕ Annuler cette offre" }}
        </button>
        <button @click="selectedOffer = null" class="btn btn-secondary">Fermer</button>
      </div>
    </div>

    <!-- Modal: Réservations -->
    <div v-if="showReservations" class="modal-overlay" @click="showReservations = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showReservations = false">✕</button>
        <h2 class="modal-title">📋 Mes réservations</h2>

        <div v-if="reservationsLoading" style="text-align:center;color:#6b7280;padding:1rem">Chargement…</div>
        <div v-else>
          <div
            v-for="res in myReservations"
            :key="res.id"
            style="background:#f9fafb;padding:1rem;border-radius:8px;margin-bottom:0.75rem"
          >
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.5rem">
              <strong style="font-size:0.875rem">
                {{ res.offerDetail && res.offerDetail.canteenName ? res.offerDetail.canteenName : "—" }}
              </strong>
              <span
                class="badge"
                :class="
                  res.status === 'CONFIRMED' ? 'badge-green' : res.status === 'CANCELLED' ? 'badge-red' : 'badge-blue'
                "
              >
                {{ res.status === "PENDING" ? "En attente" : res.status === "CONFIRMED" ? "Confirmé" : "Annulé" }}
              </span>
            </div>
            <p style="font-size:0.875rem;color:#6b7280;margin:0 0 0.5rem 0">
              {{
                res.offerDetail && res.offerDetail.items
                  ? res.offerDetail.items.dish_name + " — " + res.offerDetail.items.quantity_kg + "kg"
                  : "—"
              }}
            </p>
            <button
              v-if="res.status !== 'CANCELLED'"
              @click="cancelReservation(res)"
              style="font-size:0.75rem;color:#dc2626;background:none;border:none;cursor:pointer;padding:0"
            >
              Annuler
            </button>
          </div>
          <p v-if="myReservations.length === 0" style="text-align:center;color:#6b7280">Aucune réservation.</p>
        </div>
      </div>
    </div>

    <!-- Modal: Statistiques -->
    <div v-if="showStats" class="modal-overlay" @click="showStats = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showStats = false">✕</button>
        <h2 class="modal-title">📊 Mes statistiques</h2>

        <div class="stats-grid">
          <div class="stat-card stat-green">
            <div class="stat-icon">♻️</div>
            <div class="stat-value">{{ totalKg }}kg</div>
            <div class="stat-label">Valorisés</div>
          </div>
          <div class="stat-card stat-blue">
            <div class="stat-icon">📦</div>
            <div class="stat-value">{{ totalCollectes }}</div>
            <div class="stat-label">Collectes</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Historique -->
    <div v-if="showHistory" class="modal-overlay" @click="showHistory = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showHistory = false">✕</button>
        <h2 class="modal-title">📅 Historique des collectes</h2>

        <div class="history-list">
          <div v-for="res in allReservations" :key="res.id" class="history-item">
            <div class="history-date">
              {{ (res.offerDetail && (res.offerDetail.mealDate || res.offerDetail.meal_date)) || "—" }}
            </div>
            <div class="history-details">
              <strong>{{ res.offerDetail && res.offerDetail.canteenName ? res.offerDetail.canteenName : "—" }}</strong>
              <span>{{ res.offerDetail && res.offerDetail.items ? res.offerDetail.items.dish_name : "—" }}</span>
            </div>
            <span class="badge badge-green">{{ res.status }}</span>
          </div>
          <p v-if="allReservations.length === 0" style="text-align:center;color:#6b7280">Aucun historique.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DashboardContextHeader from "@/components/DashboardContextHeader.vue"

export default {
  name: "SurplusManagerDashboard2",
  components: { DashboardContextHeader },
  data() {
    return {
      canteenId: null,
      selectedDate: new Date().toISOString().split("T")[0],
      featureSurplus: true,
      showAllOffers: false,
      showReservations: false,
      showStats: false,
      showHistory: false,
      selectedOffer: null,
      surplusOffers: [],
      myReservations: [],
      allReservations: [],
      offersLoading: false,
      reservationsLoading: false,
      reservingId: null,
      cancellingOfferId: null,
    }
  },
  computed: {
    totalKg() {
      return this.allReservations
        .reduce((s, r) => {
          const kg = r.offerDetail && r.offerDetail.items && r.offerDetail.items.quantity_kg
          return s + (kg ? parseFloat(kg) : 0)
        }, 0)
        .toFixed(1)
    },
    totalCollectes() {
      return this.allReservations.filter((r) => r.status === "CONFIRMED").length
    },
  },
  methods: {
    onContextChange({ canteenId, selectedDate, featureSurplus }) {
      this.canteenId = canteenId
      this.selectedDate = selectedDate
      this.featureSurplus = featureSurplus ?? true
      this.loadSurplusOffers()
      this.loadMyReservations()
    },

    async loadSurplusOffers() {
      this.offersLoading = true
      try {
        const date = this.selectedDate || new Date().toISOString().split("T")[0]
        const resp = await fetch(
          `/api/v1/canteen/${this.canteenId || 0}/surplus-offers/?date=${date}&is_available=true`,
          { credentials: "same-origin" }
        )
        if (resp.ok) {
          this.surplusOffers = await resp.json()
        }
      } catch {
        // silencieux
      } finally {
        this.offersLoading = false
      }
    },

    async loadMyReservations() {
      this.reservationsLoading = true
      try {
        const resp = await fetch("/api/v1/surplus-reservations/", { credentials: "same-origin" })
        if (resp.ok) {
          const data = await resp.json()
          this.allReservations = data
          this.myReservations = data.filter((r) => r.status !== "CANCELLED")
        }
      } catch {
        // silencieux
      } finally {
        this.reservationsLoading = false
      }
    },

    getCsrfToken() {
      const match = document.cookie.match(/csrftoken=([^;]+)/)
      return match ? match[1] : ""
    },

    viewOfferDetails(offer) {
      this.selectedOffer = offer
      this.showAllOffers = false
    },

    async reserveOffer(offer) {
      this.reservingId = offer.id
      try {
        const resp = await fetch("/api/v1/surplus-reservations/", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": this.getCsrfToken() },
          body: JSON.stringify({ offer: offer.id }),
        })
        if (resp.ok) {
          this.selectedOffer = null
          this.showAllOffers = false
          await Promise.all([this.loadSurplusOffers(), this.loadMyReservations()])
        } else {
          const err = await resp.json().catch(() => ({}))
          alert("Erreur : " + JSON.stringify(err))
        }
      } catch (e) {
        alert("Erreur réseau : " + e.message)
      } finally {
        this.reservingId = null
      }
    },

    async cancelOffer(offer) {
      if (!confirm(`Annuler l'offre « ${offer.items && offer.items.dish_name ? offer.items.dish_name : "surplus"} » ?`))
        return
      this.cancellingOfferId = offer.id
      try {
        const resp = await fetch(`/api/v1/canteen/${this.canteenId}/surplus-offers/${offer.id}/`, {
          method: "PATCH",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": this.getCsrfToken() },
          body: JSON.stringify({ is_available: false }),
        })
        if (resp.ok) {
          this.selectedOffer = null
          await this.loadSurplusOffers()
        } else {
          const err = await resp.json().catch(() => ({}))
          alert("Erreur : " + (err.detail || JSON.stringify(err)))
        }
      } catch (e) {
        alert("Erreur réseau : " + e.message)
      } finally {
        this.cancellingOfferId = null
      }
    },

    async cancelReservation(reservation) {
      try {
        const resp = await fetch(`/api/v1/surplus-reservations/${reservation.id}/`, {
          method: "PATCH",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": this.getCsrfToken() },
          body: JSON.stringify({ status: "CANCELLED" }),
        })
        if (resp.ok) {
          await Promise.all([this.loadSurplusOffers(), this.loadMyReservations()])
        }
      } catch {
        // silencieux
      }
    },
  },
}
</script>

<style scoped>
/* Styles de base identiques aux autres dashboards */
.surplus-manager-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
  letter-spacing: 0.5px;
}

.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}

.card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #e5e7eb;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.card-featured {
  border: 2px solid #22c55e;
  background: linear-gradient(135deg, #f0fdf4 0%, white 100%);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.card-description {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 1rem 0;
}

.card-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  color: #2563eb;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
}

.badge-blue {
  background: #dbeafe;
  color: #1e40af;
}
.badge-orange {
  background: #fed7aa;
  color: #9a3412;
}
.badge-green {
  background: #d1fae5;
  color: #065f46;
}
.badge-red {
  background: #fecaca;
  color: #991b1b;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-large {
  max-width: 700px;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.modal-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.5rem 0;
}

/* Liste d'offres */
.offers-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.offer-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
}

.offer-card:hover {
  background: white;
  border-color: #22c55e;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.offer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.offer-school {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.offer-distance {
  font-size: 0.875rem;
  color: #6b7280;
}

.offer-details {
  margin-bottom: 0.75rem;
}

.offer-quantity {
  font-size: 1.125rem;
  font-weight: 600;
  color: #22c55e;
  margin-bottom: 0.25rem;
}

.offer-items {
  font-size: 0.875rem;
  color: #6b7280;
}

.offer-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.offer-time {
  font-size: 0.875rem;
  color: #f97316;
  font-weight: 500;
}

.btn-reserve {
  background: #22c55e;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
}

.btn-cancel-offer {
  background: none;
  color: #ef4444;
  border: 1px solid #ef4444;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  cursor: pointer;
}
.btn-cancel-offer:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-danger {
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

/* Détails */
.detail-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.info-item {
  background: #f9fafb;
  padding: 0.75rem;
  border-radius: 8px;
}

.info-label {
  display: block;
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.info-value {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a1a;
}

/* Réservations */
.reservation-card {
  background: #f9fafb;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}

.reservation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.reservation-details {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

/* Stats */
.stats-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.stat-item {
  text-align: center;
  background: #f9fafb;
  padding: 1rem;
  border-radius: 8px;
}

.stat-value {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a1a1a;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  border: 2px solid;
}

.stat-card.stat-green {
  border-color: #86efac;
  background: #f0fdf4;
}
.stat-card.stat-blue {
  border-color: #93c5fd;
  background: #eff6ff;
}
.stat-card.stat-orange {
  border-color: #fdba74;
  background: #fff7ed;
}

.stat-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

.impact-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 0.5rem;
}

.impact-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.impact-value {
  font-size: 0.875rem;
  font-weight: 700;
  color: #22c55e;
}

/* Historique */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.history-date {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  min-width: 80px;
}

.history-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.history-details strong {
  font-size: 0.875rem;
  color: #1a1a1a;
}

.history-details span {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Buttons */
.btn {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  margin-bottom: 0.75rem;
}

.btn-primary {
  background: #22c55e;
  color: white;
}

.btn-secondary {
  background: #f3f4f6;
  color: #1a1a1a;
}

@media (max-width: 640px) {
  .surplus-manager-dashboard {
    padding: 1rem;
  }
  .dashboard-title {
    font-size: 1.5rem;
  }
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
